/*
 * The code not covered is related to the behavior of the element in the browser.
 * Jest unfortunately cannot simulate interface behaviours, so these will have
 * integration tests.
 */

import * as React from 'react'
import * as classNames from 'classnames'
import * as style from './SuperHero.module.scss'
import * as csm from '../../../utils/csm'
import { HeroElement } from './HeroElement'
import DotNav from './DotNav'
import PrerollPlayer from '../../preroll/PrerollPlayer';
import { StorefrontStateCollection } from '../../../containers/Storefront';
import { StorefrontItem } from 'DVWebNodeUtils';

const NO_ACTIVE_INDEX = -1
const PREROLL_ANIMATION_DELAY = 1000
const PREROLL_CONTINUE_SLIDER_DELAY = 2000

declare let window: Window & {
  P: any
}

enum Direction {
  LEFT = -1,
  RIGHT = 1
}

const realMod = (n: number, m: number) => {
  return ((n % m) + m) % m;
}

interface SuperHeroProps extends StorefrontStateCollection {
  isScrollToPlace?: boolean
  isAutoScrollLoop?: boolean
  isWheelActive?: boolean
  isFrozen?: boolean
  autoScrollDelayMilli?: number
  animationMilli?: number
  scrollIndexDelayMilli?: number
  throttleDelayMilli?: number
  prerollPlayerComponent?: PrerollPlayer
  formFactor: string
}

interface SuperHeroState {
  loopCompleted: boolean
  index: number
  scrollLeft: number
  hasInteracted: boolean
  isJavascriptEnabled: boolean
  pausedForPlayback: boolean
}

export default class SuperHero extends React.Component<SuperHeroProps, SuperHeroState> {
  static defaultProps = {
    isScrollToPlace: false, // experimental
    isAutoScrollLoop: false,
    isWheelActive: false, // experimental
    autoScrollDelayMilli: 5000,
    animationMilli: 500,
    scrollIndexDelayMilli: 800,
    throttleDelayMilli: 1300
  }
  state = {
    index: 0,
    scrollLeft: 0,
    hasInteracted: false,
    isJavascriptEnabled: false,
    loopCompleted: false,
    pausedForPlayback: false
  }
  intervalID: number
  carouselElement: HTMLUListElement
  carouselContainer: HTMLDivElement
  rightButton: HTMLButtonElement
  items: StorefrontItem[]
  heroComponents: { [x: string]: HeroElement; } = {}
  prerollPlayerComponent: PrerollPlayer
  resizeListener: EventListener
  touchXStart = 0

  recalculateState(index: number) {
    const scrollLeft = this.carouselElement.scrollLeft
    return {
      index: typeof index === 'number' ? index : this.state.index,
      scrollLeft,
      isJavascriptEnabled: true,
      loopCompleted: this.state.loopCompleted
    }
  }

  addPlayerListeners() {
    if (typeof window !== 'undefined' && window.P) {
      window.P.when('dv-player-integration').execute((player: any) => {
        player.runBeforePlay(() => {
          if (!this.shouldNotAutoscroll()) {
            this.setState({ pausedForPlayback: true })
          }
        })
        player.on('closed', () => {
          this.setState({ pausedForPlayback: false }, () => {
            if (!this.shouldNotAutoscroll()) {
              this.moveTo(this.state.index + 1)
              this.autoScrollElements()
            }
          })
        })
      })
    }
  }

  componentDidMount() {
    if (!this.carouselElement || !this.carouselElement.children ||
      !this.carouselElement.children.length) {
      return
    }
    this.setState(this.recalculateState(this.state.index))
    this.initResizeAndTouchEvents()
    this.initTabEvents()
    if (this.canPlayPreroll() === true) {
      this.playPreroll()
    }
    this.addPlayerListeners()
  }

  componentWillUnmount() {
    if (this.intervalID) {
      this.resetInterval()
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::SuperHero')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a SuperHero')
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  initResizeAndTouchEvents() {

    this.autoScrollElements()
    this.carouselContainer.addEventListener('mouseenter', () => {
      this.resetInterval()
    })
    this.carouselContainer.addEventListener('mouseleave', () => {
      this.resetInterval()
      if (!(this.currentHeroElement() &&
        this.currentHeroElement().components.prerollPlayerComponent &&
        this.currentHeroElement().components.prerollPlayerComponent.state.playing))
        this.autoScrollElements()
    })

    // This attaches to carousel element, not Container as above, to exclude the buttons from being in the "draggable zone" 
    this.carouselElement.addEventListener('touchstart', this.handleTouchStart.bind(this))
    this.carouselElement.addEventListener('touchend', this.handleTouchEnd.bind(this))
  }

  handleTouchStart(evt: TouchEvent) {
    this.touchXStart = evt.changedTouches[0].clientX
  }

  handleTouchEnd(evt: TouchEvent) {
    const DELTAX = 20
    const newTouchX = evt.changedTouches[0].clientX
    if (Math.abs(newTouchX - this.touchXStart) > DELTAX) {
      if (evt.changedTouches[0].clientX > this.touchXStart)
        this.triggerInteractionScroll(Direction.LEFT)
      else
        this.triggerInteractionScroll(Direction.RIGHT)
    }
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  /* istanbul ignore next */
  initTabEvents() {
    const TAB_KEYCODE = 9
    const ENTER_KEYCODE = 13

    this.carouselContainer
      .addEventListener('keydown', (evt: KeyboardEvent) => {
        const prevIndexOffset = -1
        const nextIndexOffset = 1
        const currentIndex = this.state.index

        /*
       * @desc if the element is on focus we enable shortcuts in 2 ways:
       * 1. TAB key - if the tab is pressed go to the next slide unless you are
       * focusing on the last page. If you press the SHIFT key along with tab
       * go to the previous slide unless you are focusing on the firt page.
       * 2. ENTER key - if you press the enter key while focusing on the tab you will
       * be redirected to the link in the page.
       */
        if (evt.which === TAB_KEYCODE) {
          const nextIndex = currentIndex + (evt.shiftKey ? prevIndexOffset : nextIndexOffset)
          if ((evt.shiftKey && nextIndex >= 0) ||
            (!evt.shiftKey && nextIndex < this.props.items.length)) {
            evt.preventDefault()
            this.resetInterval()
            this.moveTo(nextIndex)
          }
        } else if (evt.which === ENTER_KEYCODE) {
          window.location.href = this.items[currentIndex].link.url
        }
      })
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  /* istanbul ignore next */
  updateResize() {
    this.setState(this.recalculateState(this.state.index), () => {
      this.moveTo(this.state.index)
    })
  }

  shouldComponentUpdate(nextProps: SuperHeroProps) {
    if (nextProps.items.length !== this.props.items.length) {
      this.moveTo(0)
    }

    return true
  }

  componentDidUpdate(prevProps: SuperHeroProps) {
    if (prevProps.isFrozen && !this.props.isFrozen) {
      if (this.canPlayPreroll()) {
        this.playPreroll()
      } else {
        this.autoScrollElements()
      }
    } else if (!prevProps.isFrozen && this.props.isFrozen) {
      this.stopPreroll()
    }
  }

  resetInterval() {
    clearInterval(this.intervalID)
    this.intervalID = 0
  }

  canPlayPreroll() {
    const hasHeroComponent = (this.heroComponents &&
      this.heroComponents[`heroComponent-${this.state.index}`]),
      isPlaying = this.prerollPlayerComponent && this.prerollPlayerComponent.state.playing,
      hasPlayed = this.prerollPlayerComponent && this.prerollPlayerComponent.state.hasPlayed
    return !this.props.isFrozen && hasHeroComponent && !isPlaying && !hasPlayed
  }

  playPreroll() {
    if (!this.currentHeroElement() ||
      !this.currentHeroElement().components ||
      !this.currentHeroElement().components.prerollPlayerComponent) {
      return
    }
    if (this.intervalID) {
      this.resetInterval()
    }
    setTimeout(() => !this.props.isFrozen && this.currentHeroElement() && this.currentHeroElement().components
      .prerollPlayerComponent.startPlayer(), PREROLL_ANIMATION_DELAY)
  }

  stopPreroll() {
    if (this.currentHeroElement()) {
      this.currentHeroElement().components
        .prerollPlayerComponent.stop()
    }
  }

  shouldNotAutoscroll() {
    return (this.props.isFrozen || this.state.loopCompleted ||
      (this.state.hasInteracted && !this.props.isAutoScrollLoop) || this.state.pausedForPlayback
    )
  }

  autoScrollElements(): number {
    if (this.shouldNotAutoscroll() || this.props.items[this.state.index].prerolls)
      return


    if (this.intervalID)
      return

    this.intervalID = window.setInterval(() => {
      let activeIndex = this.state.index

      if (this.state.index === this.props.items.length - 1) {
        activeIndex = NO_ACTIVE_INDEX
        this.setState({
          hasInteracted: true
        })
        if (!this.props.isAutoScrollLoop) {
          this.resetInterval()
        }
      }
      this.moveTo(activeIndex + 1)
    }, this.props.autoScrollDelayMilli)
  }

  moveTo(scrollIndex: number) {
    if (this.props.items.length < 1)
      return
    const sanitisedIndex = realMod(scrollIndex, this.props.items.length)
    const isScrollingPastEnd = scrollIndex >= this.props.items.length

    if (isScrollingPastEnd)
      this.setState({ loopCompleted: true })

    this.stopPreroll()
    if (this.carouselElement) {
      this.carouselElement.style.transform = `translateX(-${sanitisedIndex * 100}%)`
    }

    this.setState(
      this.recalculateState(sanitisedIndex),
      () => {
        if (this.canPlayPreroll() === true) {
          this.playPreroll()
        }
      }
    )
  }

  currentHeroElement() {
    return this.heroComponents[`heroComponent-${this.state.index}`]
  }

  triggerInteractionScroll(direction = Direction.RIGHT) {
    this.resetInterval()
    this.moveTo(this.state.index + direction)
    csm.count('SuperheroScroll')
  }

  /**
   *
   * @method renderChildren
   * @desc It will render child from props in the form of Hero Elements if
   * unspecified props are passed or it will render what has been passed as
   * child in a <li> element
   *
   */
  renderChildren() {
    /* 1st method: Children passed through props from WPS */
    if (this.props.items && this.props.items instanceof Array) {
      this.items = this.props.items
      return this.props.items.map((item, idx) => (item.prerolls
        ? <li key={item.image.url}>
          <HeroElement loadNow={idx < 2} item={item} formFactor={this.props.formFactor}
            hasPrerollPlayer={this.props.prerollPlayerComponent !== null} ref={(component) => {
              this.heroComponents[`heroComponent-${idx}`] = component
            }} index={idx} autoScrollFunc={() => {
              if (this.shouldNotAutoscroll()) {
                return
              }
              setTimeout(() => {
                this.moveTo(idx + 1)
                this.autoScrollElements()
              }, PREROLL_CONTINUE_SLIDER_DELAY)
            }}
            isActive={this.state.index === idx}
          />
        </li>
        : <li key={item.image.url}>
          <HeroElement loadNow={idx < 2} item={item} formFactor={this.props.formFactor} />
        </li>
      ))
    }
  }

  render() {
    return (
      <div tabIndex={0} className={classNames(style.superhero, 'tst-superhero')}
        ref={(elem) => {
          this.carouselContainer = elem
        }}>
        <div className={style.container}>
          {this.state.isJavascriptEnabled && <button
            className={classNames({ [style.hidden]: !this.state.index },
              style.handle,
              style.leftHandle)}
            value="prev"
            onClick={() => this.triggerInteractionScroll(Direction.LEFT)}
            disabled={!this.state.index}>
            <div className={style.icon}></div>
          </button>
          }
          <ul ref={(elem) => {
            this.carouselElement = elem
          }}
            className={classNames(
              'tst-superhero-item',
              style.animate,
              style.slider,
            )}>
            {this.renderChildren()}
          </ul>
          {this.state.isJavascriptEnabled && <button ref={(elem) => {
            this.rightButton = elem
          }}
            value="next"
            className={classNames(
              { [style.hidden]: this.state.index === this.props.items.length - 1 },
              style.handle,
              style.rightHandle)}
            onClick={() => this.triggerInteractionScroll(Direction.RIGHT)}
            disabled={this.state.index === this.props.items.length}>
            <div className={style.icon}></div>
          </button>
          }
          {this.props.items.length > 1 &&
            <DotNav size={this.props.items.length}
              activeIndex={this.state.index}
              moveTo={(scrollIndex: number) => {
                this.moveTo(scrollIndex)
                csm.count('SuperheroScroll')
              }}
            />
          }
        </div>
      </div>
    )
  }
}
